import React, { useEffect, useRef, useState } from "react";
import Btn from "../../CommonElements/Button";
import { X } from "react-feather";
import { socketAiAssistant as socket } from "../../Services/socketAiAssistant";
import { markdownToSafeHtml } from "./assistantMarkdownHtml";

const AiAssistantPanel = ({ onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  /* Подключение к помощнику. Пока сервер не отвечает, панель должна прямо
     сказать об этом: молчаливое поле ввода выглядит так, будто сообщение
     ушло и вот-вот придёт ответ. */
  const [online, setOnline] = useState(socket.connected);

  const listRef = useRef(null);

  const getUserId = () => {
    if (typeof window === "undefined") return null;

    const stored = window.localStorage.getItem("ai_user_id");
    if (stored) return stored;

    const token = window.localStorage.getItem("token");
    if (!token) return null;

    const parts = token.split(".");
    if (parts.length < 2) return null;

    try {
      const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(window.atob(base64));
      const uid = payload && (payload.uid || payload.user_id || payload.sub);

      if (!uid) return null;

      const uidStr = String(uid);
      window.localStorage.setItem("ai_user_id", uidStr);
      return uidStr;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);

    socket.on("connect", up);
    socket.on("disconnect", down);
    socket.on("connect_error", down);

    return () => {
      socket.off("connect", up);
      socket.off("disconnect", down);
      socket.off("connect_error", down);
    };
  }, []);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const handlePart = ({ msg_id, part_message }) => {
      setMessages((prev) => {
        const exists = prev.some((m) => m.id === msg_id);

        if (!exists) {
          return [
            ...prev,
            {
              id: msg_id,
              role: "assistant",
              text: part_message,
              isComplete: false,
            },
          ];
        }

        return prev.map((m) =>
          m.id === msg_id
            ? { ...m, text: `${m.text}${part_message}` }
            : m
        );
      });
    };

    const handleComplete = ({ msg_id, error }) => {
      if (error) {
        setMessages((prev) => [
          ...prev,
          {
            id: `${msg_id || "err"}-error`,
            role: "assistant",
            text: `Ошибка: ${error}`,
            isComplete: true,
          },
        ]);
        setIsLoading(false);
        return;
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msg_id ? { ...m, isComplete: true } : m
        )
      );
      setIsLoading(false);
    };

    socket.on("onAiMessagePart", handlePart);
    socket.on("onAiMessageComplete", handleComplete);

    return () => {
      socket.off("onAiMessagePart", handlePart);
      socket.off("onAiMessageComplete", handleComplete);
    };
  }, []);

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userId = getUserId();
    const numericUserId = userId ? Number(userId) || 1 : 1;

    const text = inputValue.trim();
    const uuid = () =>
      typeof window !== "undefined" && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;

    const userMessageId = uuid();
    const requestMsgId = uuid();

    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        role: "user",
        text,
        isComplete: true,
      },
    ]);

    setInputValue("");
    setIsLoading(true);

    const bearerToken =
      typeof window !== "undefined"
        ? window.localStorage.getItem("token") || ""
        : "";

    socket.timeout(5000).emit("onAiMessage", {
      user_id: numericUserId,
      msg_id: requestMsgId,
      message: text,
      assistant_id: 2,
      bearer_token: bearerToken,
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="ai-assistant-panel card">
      <div className="card-header ai-assistant-panel__header">
        <h5 className="mb-0">Фея Мотти</h5>
        {onClose && (
          <button
            type="button"
            className="btn btn-link p-0 ai-assistant-panel__close"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        )}
      </div>
      <div className="card-body d-flex flex-column">
        <div
          className="ai-assistant-messages flex-grow-1 mb-3"
          ref={listRef}
        >
          {messages.length === 0 && (
            <div className="ai-assistant-hello">
              <b>Здравствуйте! Я Фея Мотти.</b>
              <p>
                Помогу разобраться в кабинете: расскажу про роли, уровни,
                награды и как собрать свой первый мир. Спрашивайте своими
                словами.
              </p>
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.id + message.role}
              className={`ai-assistant-message ai-assistant-message--${message.role}`}
            >
              {message.role === "assistant" && message.isComplete ? (
                <div
                  className="ai-assistant-message__bubble ai-assistant-message__bubble--markdown"
                  dangerouslySetInnerHTML={{
                    __html: markdownToSafeHtml(message.text),
                  }}
                />
              ) : (
                <div className="ai-assistant-message__bubble">
                  {message.text}
                </div>
              )}
            </div>
          ))}
        </div>
        {!online && (
          <div className="ai-assistant-offline">
            Фея пока не отвечает — помощник ещё не подключён к кабинету.
            Ваши вопросы не потеряются, но ответа сейчас не будет.
          </div>
        )}

        <div className="ai-assistant-input">
          <textarea
            className="form-control mb-2"
            rows={3}
            placeholder="Введите сообщение..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <div className="d-flex justify-content-end">
            <Btn
              attrBtn={{
                color: "primary",
                disabled: isLoading || !inputValue.trim(),
                onClick: handleSend,
              }}
            >
              Отправить
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiAssistantPanel;

